import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, MapPin, TrendingUp, Briefcase, Building2, Trees, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const ASSET_META = {
    jv: { label: "Joint Venture", icon: Briefcase, color: "text-coral", route: (id) => `/jv/${id}` },
    listing: { label: "Land", icon: Trees, color: "text-fiji-green", route: (id) => `/properties/${id}` },
    business: { label: "Business", icon: Building2, color: "text-ocean", route: (id) => `/businesses/${id}` },
};

function fmtFjd(v) {
    const n = Number(v) || 0;
    if (!n) return "—";
    if (n >= 1_000_000) return `FJ$${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
    if (n >= 1_000) return `FJ$${Math.round(n / 1_000)}k`;
    return `FJ$${Math.round(n)}`;
}

/**
 * DealFeed — surfaces newest assets ranked by the caller's investor profile.
 * Auto-hides if the API returns no items. Drops into the buyer/investor dashboard.
 */
export default function DealFeed({ limit = 6 }) {
    const [items, setItems] = useState(null);

    useEffect(() => {
        api.get(`/deal-feed?limit=${limit}`)
            .then(({ data }) => setItems(data?.items || []))
            .catch(() => setItems([]));
    }, [limit]);

    if (items === null) {
        return (
            <Card className="p-6 mb-6" data-testid="deal-feed-loading">
                <div className="font-app text-sm text-muted-foreground">Loading your personalized feed…</div>
            </Card>
        );
    }
    if (items.length === 0) return null;

    return (
        <Card className="p-6 mb-6 bg-gradient-to-br from-ocean/5 via-background to-coral/5 border-coral/30 dark:from-ocean/15 dark:to-coral/10" data-testid="deal-feed-card">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-coral" />
                    <h3 className="font-display text-xl font-bold">Your deal feed</h3>
                    <Badge className="bg-coral/15 text-coral border-coral/30 text-[10px] uppercase tracking-wider">Personalized</Badge>
                </div>
                <span className="text-xs text-muted-foreground font-app">
                    Ranked against your investor profile
                </span>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map((it) => {
                    const meta = ASSET_META[it.asset_type] || ASSET_META.listing;
                    const Icon = meta.icon;
                    return (
                        <Link
                            key={`${it.asset_type}-${it.asset_id}`}
                            to={meta.route(it.asset_id)}
                            data-testid={`deal-feed-item-${it.asset_type}`}
                            className="group block bg-card hover:bg-secondary/40 rounded-lg border border-border p-3.5 transition"
                        >
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-app font-semibold">
                                    <Icon className={`h-3.5 w-3.5 ${meta.color}`} />
                                    <span className={meta.color}>{meta.label}</span>
                                </div>
                                <Badge variant="outline" className="text-[10px] tabular-nums">
                                    Match {it.score}
                                </Badge>
                            </div>
                            <div className="font-app font-semibold text-sm leading-snug mb-1.5 line-clamp-2 group-hover:text-coral transition">
                                {it.title || "Untitled"}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground font-app mb-2">
                                {it.location_island && (
                                    <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {it.location_island}</span>
                                )}
                                <span className="font-semibold tabular-nums">{fmtFjd(it.value_fjd)}</span>
                                {it.expected_roi_pct ? (
                                    <span className="flex items-center gap-1 text-fiji-green"><TrendingUp className="h-3 w-3" /> {it.expected_roi_pct}%</span>
                                ) : null}
                            </div>
                            {it.reasons && it.reasons.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                    {it.reasons.slice(0, 3).map((r, i) => (
                                        <span key={i} className="text-[10px] bg-secondary/60 text-muted-foreground px-1.5 py-0.5 rounded font-app">
                                            {r}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </Link>
                    );
                })}
            </div>

            <div className="mt-4 flex justify-end">
                <Button asChild variant="ghost" size="sm" className="font-app text-xs">
                    <Link to="/marketplace">Browse all <ArrowRight className="h-3 w-3 ml-1" /></Link>
                </Button>
            </div>
        </Card>
    );
}
